import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Lock, Unlock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { C } from "./AccountingLayout";

export default function AccountingSettings() {
  const [periods, setPeriods] = useState([]);
  const [rules, setRules] = useState(null);
  const [exceptions, setExceptions] = useState([]);

  const load = () => {
    api.get("/accounting/periods").then(setPeriods).catch(() => {});
    api.get("/accounting/exceptions").then(setExceptions).catch(() => {});
    api.get("/settings/posting-rules").then(setRules).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const closeP = async (p) => { if (!confirm(`Close period ${p}?`)) return; try { await api.post(`/accounting/periods/${p}/close`); toast.success("Closed"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const reopenP = async (p) => { try { await api.post(`/accounting/periods/${p}/reopen`); toast.success("Reopened"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const resolve = async (e) => { try { await api.post(`/accounting/exceptions/${e.id}/resolve`, { resolution: "manual" }); toast.success("Resolved"); load(); } catch (er) { toast.error(formatApiError(er)); } };

  return (
    <div className="space-y-4" data-testid="acc-settings-page">
      <Tabs defaultValue="periods">
        <TabsList>
          <TabsTrigger value="periods" data-testid="tab-periods">Accounting Periods</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Posting Rules</TabsTrigger>
          <TabsTrigger value="exc" data-testid="tab-exc">Exceptions ({exceptions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="periods">
          <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: C.border }}>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest border-b" style={{ color: C.softMuted, borderColor: C.borderSoft }}><th className="py-3 px-4">Period</th><th className="py-3 px-3">Status</th><th className="py-3 px-3">Opened</th><th className="py-3 px-3">Closed</th><th className="py-3 px-4 text-right">Action</th></tr></thead>
              <tbody>{periods.map((p) => (
                <tr key={p.period} className="border-b" style={{ borderColor: C.borderSoft }}>
                  <td className="py-2.5 px-4 font-semibold" style={{ color: C.text }}>{p.period}</td>
                  <td className="py-2.5 px-3"><span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: p.status === "open" ? C.greenTint : C.redTint, color: p.status === "open" ? C.green : C.red }}>{p.status === "open" ? "Open" : "Closed"}</span></td>
                  <td className="py-2.5 px-3 text-[12px]" style={{ color: C.muted }}>{(p.opened_at || "").slice(0, 10)}</td>
                  <td className="py-2.5 px-3 text-[12px]" style={{ color: C.muted }}>{(p.closed_at || "—").slice(0, 10)}</td>
                  <td className="py-2.5 px-4 text-right">{p.status === "open" ? <Button size="sm" onClick={() => closeP(p.period)} className="text-white" style={{ background: C.burgundy }} data-testid={`close-${p.period}`}><Lock size={13} className="mr-1" /> Close</Button> : <Button size="sm" variant="outline" onClick={() => reopenP(p.period)} data-testid={`reopen-${p.period}`}><Unlock size={13} className="mr-1" /> Reopen</Button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="rules">
          <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }}>
            <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>Posting Rules</h3>
            {!rules ? <div style={{ color: C.softMuted }}>No rules configured.</div> : (
              <pre className="text-[12px] bg-[#FDFBF5] p-3 rounded-lg overflow-x-auto" style={{ color: C.text }}>{JSON.stringify(rules, null, 2)}</pre>
            )}
            <div className="text-[11px] mt-3" style={{ color: C.softMuted }}>Rule editing UI ships in the next release. Rules are stored in <code>db.settings.posting_rules</code>.</div>
          </div>
        </TabsContent>

        <TabsContent value="exc">
          <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: C.border }}>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest border-b" style={{ color: C.softMuted, borderColor: C.borderSoft }}><th className="py-3 px-4">When</th><th className="py-3 px-3">Source</th><th className="py-3 px-3">Reference</th><th className="py-3 px-3">Error</th><th className="py-3 px-4 text-right">Amount</th></tr></thead>
              <tbody>{exceptions.length === 0 ? <tr><td colSpan={5} className="py-8 text-center" style={{ color: C.softMuted }}>No open exceptions.</td></tr> : exceptions.map((e) => (
                <tr key={e.id} className="border-b" style={{ borderColor: C.borderSoft }}>
                  <td className="py-2.5 px-4 text-[12px]" style={{ color: C.muted }}>{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2.5 px-3 text-[11px] uppercase font-semibold" style={{ color: C.softMuted }}>{e.source}·{e.source_event}</td>
                  <td className="py-2.5 px-3 font-mono text-[11px]">{(e.source_id || "").slice(0, 8)}</td>
                  <td className="py-2.5 px-3 text-[12px] flex items-center gap-1"><AlertTriangle size={12} style={{ color: C.red }} /> {e.error}</td>
                  <td className="py-2.5 px-4 text-right"><Button size="sm" variant="outline" onClick={() => resolve(e)}>Resolve</Button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
